import { useDispatch, useSelector } from "react-redux";

import PusherHandler from "@/api/lib/pusher/PusherHandler";
import { useEffect, useRef } from "react";
import { getBookingById, getBookingByTableAndId } from "@/api/table-floor/tableFloor";
import { addBookingToTable, removeBookingFromTable, updateTablePendingBookingCount, updateTableStatus, updateBookingInTable, fetchAllTables } from "@/redux/slice/table-slice/table-slice";
import { addNewBookingToDetails, setSelectedBookingTable, updateBookingStatusInDetails, updateBookingInDetails } from "@/redux/slice/table-slice/booking-table-slice";
import { addNewBookingToLiveDetails, updateLiveBookingDetailsBookingStatus, updateBookingInLiveDetails, removeBookingFromLiveDetails } from "@/redux/slice/table-slice/live-table-slice";

function TablePusher() {
  const dispatch = useDispatch();
  const { selectedRoom } = useSelector((state) => state.table?.table || {});
  const selectedRoomRef = useRef(selectedRoom);

  useEffect(() => {
    selectedRoomRef.current = selectedRoom;
  }, [selectedRoom]);
  
  const {
      connectionStatus: tableStatus,
      lastUpdate: lastTableUpdate,
      addEventHandler: addTableHandler,
      isConnected: tableConnected
  } = PusherHandler(); 

  useEffect(() => {
    console.log('🚀 Setting up table event handlers...');
    console.log('📡 Table connection status:', tableStatus);
    console.log('🔗 Is table connected:', tableConnected);
    
    addTableHandler('booking_created', (data) => {
      console.log('📢 Pusher booking_created event:', data);
      if(data?.booking_id){
        dispatch(setSelectedBookingTable({loading: true}));
        getBookingByTableAndId(data.table_id, data.booking_id).then(res => {
          if(res.status === "success"){
            const booking = res.data?.booking || res.data;
            if (booking) {
              console.log("✅ Fetched booking details:", booking);
              
              if(booking.status === "confirmed"){
                dispatch(addBookingToTable(booking));
              }else{
                dispatch(updateTablePendingBookingCount({ table_id: booking.table_id, increment: true}));
              }
              dispatch(addNewBookingToDetails(booking)); 
              dispatch(setSelectedBookingTable({loading: false}));
              dispatch(addNewBookingToLiveDetails(booking));
            }
          } else {
            console.error('❌ Failed to fetch booking details:', res.message);
          }

          const targetRoomId = data?.room_id || selectedRoomRef.current?.id;
          if (targetRoomId) {
            dispatch(fetchAllTables(targetRoomId));
          }
        }).catch(err => {
          console.error('❌ Error fetching booking details:', err);
          const targetRoomId = data?.room_id || selectedRoomRef.current?.id;
          if (targetRoomId) {
            dispatch(fetchAllTables(targetRoomId));
          }
        });
      } else {
        console.warn('⚠️ No booking_id found in data:', data);
        const targetRoomId = data?.room_id || selectedRoomRef.current?.id;
        if (targetRoomId) {
          dispatch(fetchAllTables(targetRoomId));
        }
      }
    });

    addTableHandler('booking_updated', (data) => {
      console.log('📢 Pusher booking_updated event:', data);
      if(data?.booking_id){
        dispatch(updateBookingInDetails(data));
        dispatch(updateBookingInLiveDetails(data));
        dispatch(updateBookingInTable(data));
        if(data?.status != data?.previous_status){
          dispatch(updateTablePendingBookingCount({ table_id: data.table_id, increment: data.status === "confirmed" ? false : true }));
        }

        if(data.status === "confirmed"){
          getBookingByTableAndId(data.table_id, data.booking_id).then(res => {
            if(res.status === "success"){
              const booking = res.data?.booking || res.data;
              if (booking) {
                console.log("✅ Fetched booking details:", booking);
                dispatch(addBookingToTable(booking));
              }
            } else {
              console.error('❌ Failed to fetch booking details:', res.message);
            }
            const targetRoomId = data?.room_id || selectedRoomRef.current?.id;
            if (targetRoomId) {
              dispatch(fetchAllTables(targetRoomId));
            }
          }).catch(err => {
            console.error('❌ Error fetching booking details:', err);
            const targetRoomId = data?.room_id || selectedRoomRef.current?.id;
            if (targetRoomId) {
              dispatch(fetchAllTables(targetRoomId));
            }
          });
        }else{
          dispatch(removeBookingFromTable({ bookingId: data.booking_id, tableId: data.table_id }));
          const targetRoomId = data?.room_id || selectedRoomRef.current?.id;
          if (targetRoomId) {
            dispatch(fetchAllTables(targetRoomId));
          }
        }
      } else {
        console.warn('⚠️ No booking_id found in data:', data);
      }
    });

    addTableHandler('table_status_updated', (data) => {
        console.log('📢 Pusher table_status_updated event:', data);
        dispatch(updateTableStatus({ tableId: data.table_id, bookingId: data.booking_id, newStatus: data.status }));
        dispatch(updateLiveBookingDetailsBookingStatus({ bookingId: data.booking_id, newStatus: data.status }));
        if(data.status == "free"){
          dispatch(removeBookingFromTable({ bookingId: data.booking_id, tableId: data.table_id }));
          dispatch(removeBookingFromLiveDetails({ bookingId: data.booking_id }));
        }
        const targetRoomId = data?.room_id || selectedRoomRef.current?.id;
        if (targetRoomId) {
          dispatch(fetchAllTables(targetRoomId));
        }
    });

    addTableHandler('reservation_table_unassigned', (data) => {
        console.log('📢 Pusher reservation_table_unassigned event:', data);
        if(data?.table_id && data?.reservation_id) {
          dispatch(updateTableStatus({ tableId: data.table_id, bookingId: data.reservation_id, newStatus: "free" }));
          dispatch(removeBookingFromTable({ bookingId: data.reservation_id, tableId: data.table_id }));
          dispatch(updateLiveBookingDetailsBookingStatus({ bookingId: data.reservation_id, newStatus: "free" }));
          dispatch(removeBookingFromLiveDetails({ bookingId: data.reservation_id }));
        }
        const targetRoomId = data?.room_id || selectedRoomRef.current?.id;
        if (targetRoomId) {
          dispatch(fetchAllTables(targetRoomId));
        }
    });

    const handleTableMergeRefresh = (data) => {
      console.log('🔄 Table merge event received via Pusher:', data);
      const targetRoomId = data?.room_id || selectedRoomRef.current?.id;
      if (targetRoomId) {
        dispatch(fetchAllTables(targetRoomId));
      }
    };

    addTableHandler('table_merge_unmerged', handleTableMergeRefresh);
    addTableHandler('table_merged', handleTableMergeRefresh);
    addTableHandler('table_unmerged', handleTableMergeRefresh);

  }, [addTableHandler, tableStatus, tableConnected, dispatch]);

  return (
    <div>

    </div>
  );
}


export default TablePusher;